"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Check, Plus, RotateCcw, Search, X } from "lucide-react";
import { matchNumberFilter } from "./logic";

/*
 * Filter builder in the style of 21st.dev "advanced data table filter builder":
 * "+ Add filter" → pick a field (searchable) → operator → value(s). Each rule
 * is a removable chip; all rules are ANDed. Preset rules (strategy defaults
 * that reproduce stockweb's server-side filter) are chips too and can be
 * removed or restored.
 */

export type FieldKind = "number" | "text" | "enum";

export interface FilterField<R> {
  id: string;
  label: string;
  kind: FieldKind;
  get: (row: R) => number | string | boolean | null | undefined;
  /** enum fields: allowed values (strings; booleans as "true"/"false"). */
  options?: string[];
}

export type FilterOp =
  "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "between" | "in" | "contains";

export interface FilterRule {
  id: string;
  field: string;
  op: FilterOp;
  value: string;
  value2?: string;
  values?: string[];
  preset?: boolean;
}

const OP_LABEL: Record<FilterOp, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  between: "between",
  in: "in",
  contains: "contains",
};

const OPS_BY_KIND: Record<FieldKind, FilterOp[]> = {
  number: ["gte", "lte", "gt", "lt", "eq", "neq", "between"],
  text: ["contains", "eq", "neq", "in"],
  enum: ["in", "eq", "neq"],
};

function matchRule(
  raw: number | string | boolean | null | undefined,
  rule: FilterRule,
  kind: FieldKind,
) {
  if (kind === "number") {
    const val = typeof raw === "number" && !Number.isNaN(raw) ? raw : null;
    // Same semantics as stockweb's matchNumberFilter: null never matches,
    // an empty/invalid bound means "no bound".
    switch (rule.op) {
      case "gte":
        return matchNumberFilter(val, "Above", rule.value, "");
      case "lte":
        return matchNumberFilter(val, "Under", rule.value, "");
      case "between":
        return matchNumberFilter(val, "Between", rule.value, rule.value2 ?? "");
      default: {
        if (val == null) return false;
        const v = parseFloat(rule.value);
        if (isNaN(v)) return true;
        if (rule.op === "gt") return val > v;
        if (rule.op === "lt") return val < v;
        if (rule.op === "eq") return val === v;
        if (rule.op === "neq") return val !== v;
        return true;
      }
    }
  }
  const s = raw == null ? "" : String(raw);
  const values = rule.values ?? [];
  switch (rule.op) {
    case "in":
      return values.length === 0 || values.includes(s);
    case "contains":
      return !rule.value || s.toLowerCase().includes(rule.value.toLowerCase());
    case "eq":
      return s.toLowerCase() === rule.value.toLowerCase();
    case "neq":
      return s.toLowerCase() !== rule.value.toLowerCase();
    default:
      return true;
  }
}

/** AND of every rule; rules pointing at an unknown field are ignored. */
export function applyRules<R>(
  rows: R[],
  rules: FilterRule[],
  fields: FilterField<R>[],
): R[] {
  if (rules.length === 0) return rows;
  const byId = new Map(fields.map((f) => [f.id, f]));
  const active = rules
    .map((rule) => ({ rule, field: byId.get(rule.field) }))
    .filter((x): x is { rule: FilterRule; field: FilterField<R> } => !!x.field);
  return rows.filter((row) =>
    active.every(({ rule, field }) =>
      matchRule(field.get(row), rule, field.kind),
    ),
  );
}

function describe<R>(
  rule: FilterRule,
  field: FilterField<R> | undefined,
): string {
  const label = field?.label ?? rule.field;
  if (rule.op === "between")
    return `${label} ${rule.value || "−∞"} – ${rule.value2 || "∞"}`;
  if (rule.op === "in") {
    const v = rule.values ?? [];
    return `${label} in (${v.length > 3 ? `${v.slice(0, 3).join(", ")} +${v.length - 3}` : v.join(", ")})`;
  }
  if (rule.op === "contains") return `${label} contains “${rule.value}”`;
  return `${label} ${OP_LABEL[rule.op]} ${rule.value}`;
}

let ruleSeq = 0;
const newRuleId = () =>
  `r${Date.now().toString(36)}${(ruleSeq++).toString(36)}`;

export const inputCls =
  "rounded-lg border border-border bg-surface-2 px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted focus:border-accent/60 focus:outline-none";

export function FilterBuilder<R>({
  fields,
  rules,
  onChange,
  presets,
  controls,
  quickFilters,
}: {
  fields: FilterField<R>[];
  rules: FilterRule[];
  onChange: (rules: FilterRule[]) => void;
  /** Strategy default rules; enables the "restore preset" button when they differ. */
  presets?: FilterRule[];
  controls?: ReactNode;
  quickFilters?: ReactNode;
}) {
  // `editing`: null = closed, "new" = adding, otherwise the id of the rule being edited.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<FilterRule | null>(null);
  const [fieldQuery, setFieldQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  const byId = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);

  const close = () => {
    setEditing(null);
    setDraft(null);
    setFieldQuery("");
  };

  // Close the popover on outside click / Escape.
  useEffect(() => {
    if (!editing) return;
    const close = () => {
      setEditing(null);
      setDraft(null);
      setFieldQuery("");
    };
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [editing]);

  const startNew = () => {
    setEditing("new");
    setDraft(null);
    setFieldQuery("");
  };

  const startEdit = (rule: FilterRule) => {
    setEditing(rule.id);
    setDraft({ ...rule, values: rule.values ? [...rule.values] : undefined });
  };

  const pickField = (f: FilterField<R>) => {
    setDraft({
      id: newRuleId(),
      field: f.id,
      op: OPS_BY_KIND[f.kind][0],
      value: "",
      values: [],
    });
  };

  const commit = () => {
    if (!draft) return;
    // An edited preset is no longer the preset value.
    const rule = { ...draft, preset: false };
    if (editing === "new") onChange([...rules, rule]);
    else onChange(rules.map((r) => (r.id === editing ? rule : r)));
    close();
  };

  const remove = (id: string) => onChange(rules.filter((r) => r.id !== id));

  const presetKey = (list: FilterRule[]) =>
    JSON.stringify(
      list.map((r) => [r.field, r.op, r.value, r.value2 ?? "", r.values ?? []]),
    );
  const presetDiffers = !!presets && presetKey(rules) !== presetKey(presets);

  const draftField = draft ? byId.get(draft.field) : undefined;
  const filteredFields = fields.filter((f) =>
    f.label.toLowerCase().includes(fieldQuery.toLowerCase()),
  );

  return (
    <div ref={boxRef} className="relative flex min-w-0 flex-col gap-3">
      <div
        className="flex flex-wrap items-start justify-between gap-3"
        data-screener-toolbar
      >
        <div className="flex min-w-0 flex-1 basis-64 flex-wrap items-center gap-2">
          {quickFilters}
          {rules.map((rule) => (
            <span
              key={rule.id}
              className={clsx(
                "inline-flex max-w-full items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-xs",
                rule.preset
                  ? "border-accent/40 bg-accent/10 text-accent"
                  : "border-border bg-surface-2 text-foreground",
              )}
            >
              <button
                type="button"
                onClick={() => startEdit(rule)}
                className="truncate tabular-nums hover:underline"
                title={
                  rule.preset
                    ? "Preset (ค่าตั้งต้นแบบ stockweb) — คลิกเพื่อแก้"
                    : "คลิกเพื่อแก้"
                }
              >
                {describe(rule, byId.get(rule.field))}
              </button>
              <button
                type="button"
                onClick={() => remove(rule.id)}
                aria-label="ลบ filter"
                className="rounded-full p-0.5 text-muted hover:bg-white/10 hover:text-foreground"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="ml-auto flex max-w-full min-w-0 flex-wrap items-center justify-end gap-2 lg:max-w-[65%]">
          {controls}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={startNew}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted hover:border-accent/50 hover:text-foreground"
        >
          <Plus size={12} /> Add filter
        </button>

        {rules.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-muted hover:text-foreground"
          >
            ล้างทั้งหมด
          </button>
        )}
        {presetDiffers && (
          <button
            type="button"
            onClick={() =>
              onChange(presets!.map((p) => ({ ...p, id: newRuleId() })))
            }
            className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
          >
            <RotateCcw size={11} /> คืนค่า preset
          </button>
        )}
      </div>

      {editing && (
        <div className="absolute left-0 top-full z-30 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-border bg-surface p-3 shadow-2xl">
          {!draft || !draftField ? (
            <>
              <div className="relative mb-2">
                <Search
                  size={13}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  autoFocus
                  value={fieldQuery}
                  onChange={(e) => setFieldQuery(e.target.value)}
                  placeholder="ค้นหา field…"
                  className={clsx(inputCls, "w-full pl-8")}
                />
              </div>
              <ul className="max-h-64 overflow-y-auto">
                {filteredFields.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => pickField(f)}
                      className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm hover:bg-white/[0.05]"
                    >
                      {f.label}
                      <span className="text-[10px] uppercase tracking-wider text-muted">
                        {f.kind}
                      </span>
                    </button>
                  </li>
                ))}
                {filteredFields.length === 0 && (
                  <li className="px-2 py-1.5 text-xs text-muted">
                    ไม่พบ field
                  </li>
                )}
              </ul>
            </>
          ) : (
            <RuleEditor
              field={draftField}
              draft={draft}
              setDraft={setDraft}
              onBack={editing === "new" ? () => setDraft(null) : undefined}
              onSubmit={commit}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RuleEditor<R>({
  field,
  draft,
  setDraft,
  onBack,
  onSubmit,
}: {
  field: FilterField<R>;
  draft: FilterRule;
  setDraft: (r: FilterRule) => void;
  onBack?: () => void;
  onSubmit: () => void;
}) {
  const ops = OPS_BY_KIND[field.kind];
  const values = draft.values ?? [];
  const toggleValue = (v: string) =>
    setDraft({
      ...draft,
      values: values.includes(v)
        ? values.filter((x) => x !== v)
        : [...values, v],
    });

  return (
    <form
      className="flex flex-col gap-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{field.label}</span>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-muted hover:text-foreground"
          >
            เปลี่ยน field
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {ops.map((op) => (
          <button
            key={op}
            type="button"
            onClick={() => setDraft({ ...draft, op })}
            className={clsx(
              "rounded-md border px-2 py-0.5 text-xs",
              draft.op === op
                ? "border-accent/50 bg-accent/15 text-accent"
                : "border-border text-muted hover:text-foreground",
            )}
          >
            {OP_LABEL[op]}
          </button>
        ))}
      </div>

      {draft.op === "in" ? (
        field.options && field.options.length > 0 ? (
          <ul className="max-h-48 overflow-y-auto rounded-lg border border-border">
            {field.options.map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  onClick={() => toggleValue(opt)}
                  className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-white/[0.05]"
                >
                  <span
                    className={clsx(
                      "flex h-3.5 w-3.5 items-center justify-center rounded border",
                      values.includes(opt)
                        ? "border-accent bg-accent text-white"
                        : "border-border",
                    )}
                  >
                    {values.includes(opt) && <Check size={10} />}
                  </span>
                  {opt}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <input
            autoFocus
            value={values.join(", ")}
            onChange={(e) =>
              setDraft({
                ...draft,
                values: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="ค่า คั่นด้วย ,"
            className={inputCls}
          />
        )
      ) : field.kind === "enum" && field.options ? (
        <select
          value={draft.value}
          onChange={(e) => setDraft({ ...draft, value: e.target.value })}
          className={inputCls}
        >
          <option value="">— เลือก —</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex items-center gap-1.5">
          <input
            autoFocus
            type={field.kind === "number" ? "number" : "text"}
            step="any"
            value={draft.value}
            onChange={(e) => setDraft({ ...draft, value: e.target.value })}
            placeholder={draft.op === "between" ? "Min" : "Value"}
            className={clsx(inputCls, "min-w-0 flex-1")}
          />
          {draft.op === "between" && (
            <>
              <span className="text-muted">–</span>
              <input
                type="number"
                step="any"
                value={draft.value2 ?? ""}
                onChange={(e) => setDraft({ ...draft, value2: e.target.value })}
                placeholder="Max"
                className={clsx(inputCls, "min-w-0 flex-1")}
              />
            </>
          )}
        </div>
      )}

      <button
        type="submit"
        className="rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
      >
        ใช้ filter
      </button>
    </form>
  );
}
